import { Component, DestroyRef, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { catchError, EMPTY, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiTableModule } from 'omnium-ui/table';
import { OuiTagModule } from 'omnium-ui/tag';
import { OuiTabsModule } from 'omnium-ui/tabs';
import { TechnicalCacheStatus, TechnicalCachesStatus } from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import {
  SummaryGridComponent,
  SummaryGridItem
} from '../../shared/summary-grid/summary-grid.component';

@Component({
  selector: 'dbw-technical-caches-page',
  imports: [
    MatTableModule,
    OuiButtonModule,
    OuiTableModule,
    OuiTagModule,
    OuiTabsModule,
    StateCardComponent,
    SummaryGridComponent
  ],
  templateUrl: './technical-caches.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./technical-caches.component.scss']
})
export class TechnicalCachesPageComponent {
  readonly displayedColumns = ['cache', 'entries', 'feedingMode', 'refresh', 'expiration'];
  readonly loading = signal(true);
  readonly error = signal<string | undefined>(undefined);
  readonly response = signal<TechnicalCachesStatus | undefined>(undefined);
  readonly summaryItems = computed<SummaryGridItem[]>(() => {
    const response = this.response();

    if (!response) {
      return [];
    }

    const caches = response.caches;
    const totalEntries = caches.reduce((sum, cache) => sum + cache.entryCount, 0);
    const refreshingCount = caches.filter(cache => cache.isRefreshInProgress).length;

    return [
      {
        key: 'caches',
        label: 'Caches suivis',
        value: caches.length,
        meta: 'Investisseur Atlas et Box Placement.'
      },
      {
        key: 'entries',
        label: 'Entrées connues',
        value: totalEntries,
        meta: 'Somme des entrées actuellement chargées.'
      },
      {
        key: 'refreshing',
        label: 'Chargements actifs',
        value: refreshingCount,
        meta: refreshingCount > 0 ? 'Au moins un cache est en cours d’alimentation.' : 'Aucun chargement en cours.',
        aside: {
          label: refreshingCount > 0 ? 'Actif' : 'Stable',
          status: refreshingCount > 0 ? 'pending' : 'completed'
        }
      },
      {
        key: 'transaction-mode',
        label: 'Mode transaction',
        value: response.status.transactionMode,
        meta: 'Atlas: ' + response.status.atlasDatabase + ' | Minotaure: ' + response.status.minotaureDatabase,
        aside: {
          label: response.status.rollbackInsteadOfCommit ? 'Simulation' : 'Exécution',
          status: response.status.rollbackInsteadOfCommit ? 'pending' : 'completed'
        }
      }
    ];
  });

  private readonly destroyRef = inject(DestroyRef);

  constructor(private readonly apiService: DoublonsBusterApiService) {
    timer(0, 5000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY)
      )
      .subscribe(() => {
        this.load();
      });
  }

  reload(): void {
    this.load(true);
  }

  getCacheStatusTag(cache: TechnicalCacheStatus): 'completed' | 'pending' | 'todo' {
    if (cache.isRefreshInProgress || cache.isWarmupInProgress) {
      return 'pending';
    }

    return cache.entryCount > 0 || cache.isWarmupCompleted ? 'completed' : 'todo';
  }

  getCacheStatusLabel(cache: TechnicalCacheStatus): string {
    if (cache.isWarmupInProgress) {
      return 'Warmup en cours';
    }

    if (cache.isRefreshInProgress) {
      return 'Chargement en cours';
    }

    if (cache.isWarmupCompleted) {
      return 'Warmup terminé';
    }

    return cache.entryCount > 0 ? 'Disponible' : 'En attente';
  }

  getWarmupTag(cache: TechnicalCacheStatus): 'completed' | 'pending' | 'todo' {
    if (cache.isWarmupInProgress) {
      return 'pending';
    }

    return cache.isWarmupCompleted ? 'completed' : 'todo';
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return 'Non renseigné';
    }

    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'medium'
    }).format(new Date(value));
  }

  private load(forceLoadingIndicator = false): void {
    if (forceLoadingIndicator || !this.response()) {
      this.loading.set(true);
    }

    this.apiService.getTechnicalCachesStatus()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(error => {
          this.error.set(error?.error?.detail ?? 'Impossible de charger les informations techniques.');
          this.loading.set(false);
          return EMPTY;
        })
      )
      .subscribe(response => {
        this.response.set(response);
        this.error.set(undefined);
        this.loading.set(false);
      });
  }
}
